/**
 * Quantity-mode creation (MDOT-25812) AUM and base-currency dialog: the first thing a portfolio
 * created in quantities asks for, before its composition can be built. Every quantity is sized
 * against the AUM, in the base currency, so the dialog cannot be dismissed, and its one way out
 * other than confirming leaves the creation page. The currency stays fixed from then on; the AUM
 * changes only through the negative-cash banner's `../AumDialog`.
 */
import { getApiGen } from "$root/api/factory";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { DEFAULT_CURRENCY, formatCurrencyAmount } from "$root/utils/currency";
import { type Currencies, EntityEditorControllerApiFactory } from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogFooter } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { Button, type SelectOption, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { useController, useForm } from "react-hook-form";
import { z } from "zod";
import { MAX_QUANTITY_AUM } from "../AumDialog";
import { AumCurrencyInput } from "./AumCurrencyInput";

/** What the dialog resolves on confirm: the fixed budget and the currency it is held in. */
export type CreationAum = { aum: number; currency: Currencies };

type CreationAumDialogProps = {
	/** The base currency pre-selected; {@link DEFAULT_CURRENCY} when the host has none. */
	defaultCurrency?: Currencies;
	/** Leaves the creation page — the dialog's only way out other than confirming. */
	onLeave(): void;
};

/** The currencies a new portfolio may be created in, as the editor's new-entity main info lists them. */
function useSelectableCurrencies() {
	return useQueryNoRefetch({
		queryKey: ["selectable", "currencies"],
		queryFn: async (): Promise<SelectOption<Currencies>[]> => {
			const selectable = await axiosExtract(
				getApiGen(EntityEditorControllerApiFactory).getEditorNewSelectableMainInfo(),
			);
			return (selectable.availableCurrencies ?? []).map((currency) => ({ label: currency, value: currency }));
		},
	});
}

type CreationAumFields = { currency: Currencies; aum: number | null };

const creationAumSchema = z
	.object({
		currency: z.custom<Currencies>((value) => typeof value === "string" && value !== "", "Please select a currency"),
		aum: z
			.number({
				required_error: "Please provide the portfolio AUM",
				invalid_type_error: "Please provide the portfolio AUM",
			})
			.gt(0, "The portfolio AUM must be greater than zero"),
	})
	// The cap names the chosen currency, so it is checked against the whole form.
	.superRefine(({ currency, aum }, ctx) => {
		if (aum > MAX_QUANTITY_AUM) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["aum"],
				message: `The portfolio AUM cannot exceed ${formatCurrencyAmount(MAX_QUANTITY_AUM, currency, 0)}`,
			});
		}
	});

function CreationAumDialog({
	show,
	onAnimationStateChange,
	defaultCurrency,
	onLeave,
	onConfirm,
}: CreationAumDialogProps & Omit<MinimumDialogProps, "onClose"> & { onConfirm(value: CreationAum): void }) {
	const { control, formState, handleSubmit } = useForm<CreationAumFields>({
		defaultValues: { currency: defaultCurrency ?? DEFAULT_CURRENCY, aum: null },
		mode: "onChange",
		resolver: zodResolver(creationAumSchema),
	});
	const { field: currencyField } = useController({ control, name: "currency" });
	const { data: currencies } = useSelectableCurrencies();

	return (
		// No `onClose`: neither Escape nor the backdrop can dismiss it, and there is no close icon.
		<Dialog
			show={show}
			header="AUM and currency"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onLeave} data-qualifier="CompositionEditor/CreationAumDialog/Cancel">
							Cancel
						</Button>
					}
					primaryAction={
						<SubmitButton disabled={!formState.isValid} data-qualifier="CompositionEditor/CreationAumDialog/Confirm">
							Confirm
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => handleSubmit(({ aum, currency }) => onConfirm({ aum: aum!, currency }))()}
		>
			<FormFields.Custom control={control} formState={formState} name="aum" label="AUM">
				{({ id, invalid, field }) => (
					<AumCurrencyInput
						id={id}
						invalid={invalid}
						aum={field.value}
						onAumChange={field.onChange}
						onAumBlur={field.onBlur}
						currency={currencyField.value}
						onCurrencyChange={currencyField.onChange}
						currencies={currencies}
						qualifier="CompositionEditor/CreationAumDialog"
					/>
				)}
			</FormFields.Custom>
		</Dialog>
	);
}

/**
 * Asks for the AUM and base currency of a portfolio created in quantities. Resolves them on confirm,
 * or `null` once the user has chosen to leave (after `onLeave` has run).
 */
export function spawnCreationAumDialog({
	signal,
	...props
}: CreationAumDialogProps & {
	/** Takes the dialog down, rejecting its promise: for a host unmounted while it is open. */
	signal?: AbortSignal;
}): SpawnResult<CreationAum | null> {
	return spawn<CreationAum | null>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<CreationAumDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onLeave={() => {
					props.onLeave();
					resolve(null);
				}}
				onConfirm={resolve}
			/>
		)),
		{ signal },
	);
}
